import { Children, isValidElement, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Check, ChevronDown } from "lucide-react";

// A drop-in for <select>: same props (value or defaultValue, onChange(event), id, name,
// aria-label, disabled, className) and the same <option>/<optgroup> children, drawn as
// the app's own menu instead of the browser's list.
//
// A hidden native <select> still holds the value, so a form, a label's htmlFor and the
// tests that set .value and fire "change" keep working. The button is what people use.
// The menu is portalled and fixed to the button, so a card or sheet that clips its
// overflow never cuts it off. Colours read the page's tokens, with light fallbacks.

const textOf = (node) =>
  Children.toArray(node)
    .map((c) => (typeof c === "string" || typeof c === "number" ? String(c) : isValidElement(c) ? textOf(c.props.children) : ""))
    .join("");

function readOptions(children, group = "") {
  const out = [];
  Children.toArray(children).forEach((child) => {
    if (!isValidElement(child)) return;
    if (child.type === "optgroup") out.push(...readOptions(child.props.children, child.props.label));
    else if (child.type === "option") {
      const label = textOf(child.props.children);
      out.push({ value: String(child.props.value ?? label), label, disabled: !!child.props.disabled, group });
    } else if (typeof child.type === "function" && !child.type.prototype?.isReactComponent) {
      // A small component that only returns options (StepOptionGroups): read what it returns.
      out.push(...readOptions(child.type(child.props), group));
    } else if (child.props?.children) out.push(...readOptions(child.props.children, group));
  });
  return out;
}

export default function Dropdown({
  value,
  defaultValue,
  onChange,
  children,
  className = "",
  disabled = false,
  id,
  name,
  required,
  title,
  style,
  "aria-label": ariaLabel,
  "aria-labelledby": labelledBy,
  "aria-describedby": describedBy,
  "aria-invalid": invalid,
  "data-testid": testid,
}) {
  const options = readOptions(children);
  const [own, setOwn] = useState(defaultValue ?? options[0]?.value ?? "");
  const current = String(value !== undefined ? value : own);
  const chosen = options.find((o) => o.value === current);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [place, setPlace] = useState(null);
  const button = useRef(null);
  const menu = useRef(null);
  const listId = useId();
  const autoId = useId();
  const buttonId = id ? `${id}-button` : autoId;

  const emit = (v) => {
    if (value === undefined) setOwn(v);
    onChange?.({ target: { value: v, name, id }, currentTarget: { value: v, name, id } });
  };
  const pick = (o) => {
    setOpen(false);
    button.current?.focus();
    if (!o.disabled && o.value !== current) emit(o.value);
  };
  const show = () => {
    if (disabled || !options.length) return;
    setActive(Math.max(0, options.findIndex((o) => o.value === current)));
    setOpen(true);
  };
  const move = (from, step) => {
    for (let i = 1; i <= options.length; i += 1) {
      const n = (from + step * i + options.length * i) % options.length;
      if (!options[n].disabled) return n;
    }
    return from;
  };

  // Placed under the button, or above it when there is no room below.
  useLayoutEffect(() => {
    if (!open) return undefined;
    const placeIt = () => {
      const r = button.current.getBoundingClientRect();
      const room = window.innerHeight - r.bottom - 8;
      const height = Math.min(288, options.length * 36 + 8);
      const up = room < height && r.top > room;
      setPlace({ left: r.left, width: r.width, top: up ? undefined : r.bottom + 4, bottom: up ? window.innerHeight - r.top + 4 : undefined });
    };
    placeIt();
    window.addEventListener("resize", placeIt);
    window.addEventListener("scroll", placeIt, true);
    return () => {
      window.removeEventListener("resize", placeIt);
      window.removeEventListener("scroll", placeIt, true);
    };
  }, [open, options.length]);

  useEffect(() => {
    if (!open) return undefined;
    const away = (e) => !button.current?.contains(e.target) && !menu.current?.contains(e.target) && setOpen(false);
    document.addEventListener("mousedown", away);
    return () => document.removeEventListener("mousedown", away);
  }, [open]);

  useEffect(() => {
    if (open) menu.current?.querySelector(`[data-index="${active}"]`)?.scrollIntoView?.({ block: "nearest" });
  }, [open, active]);

  const onKey = (e) => {
    if (!open) {
      if (["ArrowDown", "ArrowUp", "Enter", " "].includes(e.key)) {
        e.preventDefault();
        show();
      }
      return;
    }
    if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
    } else if (e.key === "Tab") setOpen(false);
    else if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => move(a, e.key === "ArrowDown" ? 1 : -1));
    } else if (e.key === "Home" || e.key === "End") {
      e.preventDefault();
      setActive(e.key === "Home" ? move(-1, 1) : move(options.length, -1));
    } else if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      pick(options[active]);
    } else if (e.key.length === 1) {
      const n = options.findIndex((o, i) => i > active && o.label.toLowerCase().startsWith(e.key.toLowerCase()));
      const m = n >= 0 ? n : options.findIndex((o) => o.label.toLowerCase().startsWith(e.key.toLowerCase()));
      if (m >= 0) setActive(m);
    }
  };

  let lastGroup = "";
  return (
    <>
      <select
        id={id}
        name={name}
        value={current}
        required={required}
        disabled={disabled}
        onChange={(e) => emit(e.target.value)}
        aria-label={ariaLabel}
        aria-hidden="true"
        tabIndex={-1}
        data-testid={testid}
        className="sr-only"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value} disabled={o.disabled}>
            {o.label}
          </option>
        ))}
      </select>
      <button
        ref={button}
        id={buttonId}
        type="button"
        disabled={disabled}
        title={title}
        style={style}
        onClick={() => (open ? setOpen(false) : show())}
        onKeyDown={onKey}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-label={ariaLabel ? `${ariaLabel}: ${chosen?.label || ""}` : undefined}
        aria-labelledby={labelledBy}
        aria-describedby={describedBy}
        aria-invalid={invalid}
        data-testid={testid ? `${testid}-button` : undefined}
        className={`inline-flex min-w-0 items-center justify-between gap-2 text-left disabled:cursor-not-allowed disabled:opacity-60 ${disabled ? "" : "cursor-pointer"} ${className}`}
      >
        <span className={`min-w-0 truncate ${chosen ? "" : "opacity-60"}`}>{chosen?.label || "Choose"}</span>
        <ChevronDown size={15} aria-hidden="true" className={`shrink-0 opacity-60 transition-transform duration-150 ${open ? "rotate-180" : ""}`} />
      </button>
      {open &&
        place &&
        createPortal(
          <ul
            ref={menu}
            id={listId}
            role="listbox"
            aria-label={ariaLabel || chosen?.label}
            style={{ position: "fixed", left: place.left, top: place.top, bottom: place.bottom, minWidth: Math.max(place.width, 160), maxWidth: Math.max(place.width, 360) }}
            className="z-[200] m-0 max-h-72 list-none overflow-auto rounded-xl border border-[var(--line,#EAECF0)] bg-[var(--surface,#FFFFFF)] p-1 text-[14px] text-[var(--ink,#111827)] shadow-[0_4px_8px_-2px_rgba(16,24,40,.08),0_16px_32px_-6px_rgba(16,24,40,.14)]"
          >
            {options.map((o, n) => {
              const head = o.group && o.group !== lastGroup;
              lastGroup = o.group;
              return [
                head ? (
                  <li key={`g-${o.group}`} role="presentation" className="px-[10px] pb-1 pt-2 text-[11.5px] font-semibold uppercase tracking-[.05em] text-[var(--muted,#6B7280)]">
                    {o.group}
                  </li>
                ) : null,
                <li
                  key={o.value}
                  role="option"
                  data-index={n}
                  aria-selected={o.value === current}
                  aria-disabled={o.disabled || undefined}
                  onMouseEnter={() => !o.disabled && setActive(n)}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => pick(o)}
                  className={`flex items-center gap-2 rounded-lg px-[10px] py-[7px] ${o.disabled ? "cursor-default opacity-40" : "cursor-pointer"} ${n === active && !o.disabled ? "bg-[var(--soft,#F3F4F6)]" : ""}`}
                >
                  <span className={`min-w-0 grow truncate ${o.value === current ? "font-medium" : ""}`}>{o.label}</span>
                  {o.value === current && <Check size={15} aria-hidden="true" className="shrink-0 text-[var(--royal,#2667B5)]" />}
                </li>,
              ];
            })}
          </ul>,
          document.body,
        )}
    </>
  );
}
